/** Premium-paywall A/B (ENG-348): arm names, kill switch and the arm cookie both apps share.
 *  Design log: docs/eng-348-premium-pricing-ab.md. */

import { readCookie, setCookie, sharedCookieDomain } from "@medceptor/cookie";

/** The PostHog flag, `feature_flag_assignments.flag_key` and the events' `experiment`. */
export const PREMIUM_PAYWALL_EXPERIMENT = "premium_paywall";

export const PREMIUM_PAYWALL_ARMS = ["control", "premium"] as const;
export type PremiumPaywallArm = (typeof PREMIUM_PAYWALL_ARMS)[number];

export function isPremiumPaywallArm(value: unknown): value is PremiumPaywallArm {
  return (PREMIUM_PAYWALL_ARMS as readonly unknown[]).includes(value);
}

/** Kill switch: anything but "on" means no draws, existing cookies ignored, every paywall control. */
export function premiumPaywallEnabled(value: string | undefined): boolean {
  return value === "on";
}

/** Bots Next's `isBot` misses, never drawn (a crawler would index a noindex arm). Use with `isBot`, never instead.
 *  An empty UA is a script; `(?<!cu)` spares CUBOT phones. */
const AUTOMATED_UA =
  /(?<!cu)bot|crawl|spider|slurp|googleother|google-inspectiontool|lighthouse|pagespeed|headless|curl|wget|python|httpclient|http-client|okhttp|axios|node-fetch|facebookexternalhit|embedly|preview/i;

export function isAutomatedUserAgent(ua: string | null | undefined): boolean {
  return !ua || AUTOMATED_UA.test(ua);
}

const PREMIUM_PAYWALL_COOKIE = "mc_arm_pp";

/** Six months, past the readout's 180-day horizon (D3), so a returning visitor keeps their arm. */
export const ARM_COOKIE_MAX_AGE_SECONDS = 15_552_000;

export function readPremiumPaywallCookie(
  cookieString: string | null | undefined,
): PremiumPaywallArm | null {
  const value = readCookie(cookieString, PREMIUM_PAYWALL_COOKIE);
  return isPremiumPaywallArm(value) ? value : null;
}

export function premiumPaywallArmCookie(input: {
  arm: PremiumPaywallArm;
  hostname: string;
  secure: boolean;
}): string {
  return setCookie(PREMIUM_PAYWALL_COOKIE, input.arm, {
    domain: sharedCookieDomain(input.hostname),
    secure: input.secure,
    maxAge: ARM_COOKIE_MAX_AGE_SECONDS,
  });
}

/** Host-only: the user the arm cookie was last reconciled to, so the reconcile runs once per (browser, user). */
const PREMIUM_PAYWALL_SYNC_COOKIE = "mc_arm_sync";

export function readPremiumPaywallSyncCookie(
  cookieString: string | null | undefined,
): string | null {
  return readCookie(cookieString, PREMIUM_PAYWALL_SYNC_COOKIE) || null;
}

export function premiumPaywallSyncCookie(input: {
  userId: string;
  secure: boolean;
}): string {
  return setCookie(PREMIUM_PAYWALL_SYNC_COOKIE, input.userId, {
    secure: input.secure,
    maxAge: ARM_COOKIE_MAX_AGE_SECONDS,
  });
}
